import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import postData from './postData';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz } from 'ag-grid-community'
import { purpleairStatus, purpleairColor } from './purpleairfunctions';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText, Box, Typography } from '@mui/material';
import PageSensitiveGroup from './pageSensitiveGroup';

const PurpleairWebpageIncident = () => {
  //arrays to hold sensor readings
  const [aryIncident, setIncident] = useState(undefined);
  const [retryCount, setRetryCount] = useState(0);

  //warning dialog variables
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [openSensitiveDialog, setOpenSensitiveDialog] = useState(false);

  //  grid variables
  const gridRef = useRef();
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);

  function statusValueGetter(params) {
    return purpleairStatus(params.data.max25);
  }

  const formatDate = (params) => {
    let thedate = new Date(params.data.datetimestart);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(thedate);
  };

  function statusColor(params) {
    let bgcolor = purpleairColor(params.data.max25);
    return { backgroundColor: bgcolor }
  }

  function diffMinutes(params) {
    let dateStart = new Date(params.data.datetimestart);
    let dateEnd = new Date(params.data.datetimeend);
    const diffInMs = dateEnd - dateStart; // difference in milliseconds
    if (diffInMs < 0) {return 'current conditions'};
    let numMin = Math.round(diffInMs / (1000 * 60)); // convert ms to minute
    if (numMin > 60) {
      let numHour = Math.trunc(numMin / 60);
      let remainMin = numMin - numHour * 60;
      let strResult = `${numHour} hour`;
      if  (numHour > 1) {strResult = strResult + 's'};
      if (remainMin > 0) {
        strResult = strResult + ` and ${remainMin} minutes`;
      }
      return strResult
    } else {

      return `${numMin} minutes`
    } //else > 60;
  }

  const columnDefs = [
    { headerName: 'Beginning on', valueGetter: formatDate, width: 270 }
    , { headerName: 'Spike Risk Level', valueGetter: statusValueGetter , cellStyle: statusColor}
    , { headerName: 'Duration', valueGetter: diffMinutes }
    , { headerName: 'Concentration', field: 'max25' }
  ];

  const defaultColDef = {
    sortable: true,
    autoHeight: true,
    resizable: true,
    suppressMovable: true
  };

  const gridTheme = themeQuartz.withParams({
    fontSize: 12
  })


  const popupParent = useMemo(() => {
    return document.body;
  }, []);

  const onBtnExport = useCallback(() => {
    gridRef.current.api.exportDataAsCsv();
  }, []);


  const autoSizeStrategy = useMemo(() => ({
    type: 'fitCellContents'
  }), []);

  //-------------------------------------------------------------
  // ----- handle user warning
  useEffect(() => {
    if (errorMessage !== '') {
      setOpenWarningDialog(true);
    }
  }, [errorMessage]
  )
  const handleErrorDialogOK = () => {
    setOpenWarningDialog(false);
    setErrorMessage('');
  };

  //-------------------------------------------------------------  
  // retrieve relevant submissions ------------------------------
  const getIncidents = async () => {

    const formData = new FormData();
    var objEntity = await postData('https://sarasotapublic.com/usrecycling/purpleairWebpageIncident.php', formData);
    setErrorMessage(objEntity.theError);
    if (objEntity.theData !== null) {
      setIncident(objEntity.theData);
    } else {
      if (retryCount < 3) {
        // Retry after 3 seconds
        setTimeout(() => setRetryCount(retryCount + 1), 3000);
      } //retry
    } //else 
  }

  useEffect(() => {
    getIncidents();
    // eslint-disable-next-line
  }, [retryCount]);


  //----------------------------------------------------------------------
  const handleClickShowSensitive = (e) => {
    setOpenSensitiveDialog(true);
  };
  const handleSensitiveDialogOK = () => {
    setOpenSensitiveDialog(false);
  };
  //-------------------------------------------------------------
  return (
    <><div style={{textAlign:'left'}}>
        <Box display="flex" alignItems="center" gap={10}>
        <Typography variant="h5" gutterBottom>
          Prior incidents of hazardous readings
        </Typography>
        <Button variant='contained' onClick={onBtnExport} >Export</Button>
      </Box>
      <div id="gridContainer" style={{ height: '75vh', width: '100%', textAlign: 'left' }}>
        <div style={gridStyle} className="ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            rowData={aryIncident}
            suppressExcelExport={true}
            popupParent={popupParent}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            theme={gridTheme}
            autoSizeStrategy={autoSizeStrategy}
          />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginLeft:'10px',marginRight:'10px' }}>
        <span style={{ fontSize: '0.8em', fontStyle: 'italic' }}>values of PM 2.5 ( µg/m<sub>3</sub> ) taken on a two minute interval -- incident reporting began on May 22, 2025</span>
        <button onClick={handleClickShowSensitive}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            margin: 0,
            color: 'blue',
            textDecoration: 'underline',
            cursor: 'pointer',
            font: 'inherit',
          }} ><span style={{ fontSize: '0.8em' }}>who are considered at risk</span></button>
      </div>
          </div>
      <Dialog
        open={openWarningDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Submission List Error
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {errorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleErrorDialogOK}>Ok</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openSensitiveDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        scroll="paper"
      >
        <DialogTitle id="alert-dialog-title">
          Who are considered sensitive to airborne concrete dust
        </DialogTitle>
        <DialogContent>
          <div style={{maxHeight:'40vh'}}>
            <PageSensitiveGroup /></div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSensitiveDialogOK}>Ok</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PurpleairWebpageIncident;